'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/Dialog';
import { Button } from './ui/Button';
import { useErrorDialog } from './ui/ErrorDialog';
import { useToast } from '../contexts';
import TermsDialog from './TermsDialog';

/* =========================
   Backend response (seu controller)
   ========================= */
type RegisterApiResponse = {
  status: '1' | '2' | '3' | 'Erro' | string;
  message: string;
  retryAfterSeconds?: number;
  remaining?: number;
};

type Step = 'form' | 'code' | 'success';

type RegisterForm = {
  name: string;
  loginId: string; // "id"
  password: string;
  confirmPassword: string;
  phoneDigits: string; // somente dígitos
  acceptTerms: boolean;
};

type RegisterVerifyDialogProps = {
  open: boolean;

  registerRequest: (body: {
    Username: string;
    Password: string;
    Name: string;
    Cellphone: string; // E.164
    Code?: string | null;
  }) => Promise<RegisterApiResponse>;

  /** link do grupo whatsapp (manda o seu real aqui) */
  whatsappGroupUrl: string;
};

import { onlyDigits, COUNTRIES, type Country, toE164, phonePlaceholder, formatPhone } from '../utils/phone';
import CountrySelector from './CountrySelector';

/* ---------------- OTP Input ---------------- */

function OTPInput({
  length = 6,
  value,
  onChange,
  disabled
}: {
  length?: number;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const vals = useMemo(() => {
    const clean = onlyDigits(value).slice(0, length);
    return Array.from({ length }, (_, i) => clean[i] ?? '');
  }, [value, length]);

  const focusAt = (i: number) => {
    refs.current[i]?.focus();
    refs.current[i]?.select();
  };

  const setChar = (i: number, char: string) => {
    const clean = onlyDigits(char).slice(-1);
    const arr = [...vals];
    arr[i] = clean;
    onChange(arr.join(''));
    if (clean && i < length - 1) focusAt(i + 1);
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (vals[i]) {
        setChar(i, '');
      } else if (i > 0) {
        focusAt(i - 1);
        const arr = [...vals];
        arr[i - 1] = '';
        onChange(arr.join(''));
      }
    }
    if (e.key === 'ArrowLeft' && i > 0) focusAt(i - 1);
    if (e.key === 'ArrowRight' && i < length - 1) focusAt(i + 1);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = onlyDigits(e.clipboardData.getData('text')).slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const idx = Math.min(text.length - 1, length - 1);
    requestAnimationFrame(() => focusAt(idx));
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={vals[i]}
          disabled={disabled}
          onPaste={onPaste}
          onKeyDown={(e) => onKeyDown(i, e)}
          onChange={(e) => setChar(i, e.target.value)}
          className={[
            'h-12 w-11 rounded-xl border border-white/10 bg-white/5 text-center text-xl font-extrabold text-white',
            'outline-none backdrop-blur-xl',
            'focus:border-[#00E1D7]/60 focus:ring-2 focus:ring-[#00E1D7]/20',
            disabled ? 'opacity-60' : 'hover:bg-white/10'
          ].join(' ')}
        />
      ))}
    </div>
  );
}

/* ---------------- Dialog (form + code + success) ---------------- */

export default function RegisterVerifyDialog({
  open,
  registerRequest,
  whatsappGroupUrl
}: RegisterVerifyDialogProps) {
  const { showError } = useErrorDialog();
  const { openRegister, setOpenRegister } = useToast();

  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [showTermsInRegister, setShowTermsInRegister] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [otp, setOtp] = useState('');

  const [cooldown, setCooldown] = useState(0);

  const applyCooldown = (seconds?: number) => {
    const s = typeof seconds === 'number' && seconds > 0 ? seconds : 60;
    setCooldown((cur) => Math.max(cur, s));
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const [country, setCountry] = useState<Country>(COUNTRIES.find(c => c.iso === 'br')!);

  const [form, setForm] = useState<RegisterForm>({
    name: '',
    loginId: '',
    password: '',
    confirmPassword: '',
    phoneDigits: '',
    acceptTerms: false
  });

  const phoneMasked = formatPhone(country, form.phoneDigits);
  const phoneE164 = toE164(country, form.phoneDigits);
  const phoneValid = Boolean(phoneE164);
  const otpValid = otp.length === 6;

  const payload = useMemo(() => {
    return {
      name: form.name.trim(),
      loginId: form.loginId.trim(),
      password: form.password,
      phoneDigits: onlyDigits(form.phoneDigits),
      acceptTerms: form.acceptTerms,
      phoneE164
    };
  }, [form, phoneE164]);

  useEffect(() => {

    if (!open) {
      setStep('form');
      setLoading(false);
      setError(null);
      setOtp('');
      setCooldown(0);
      setCountry(COUNTRIES.find(c => c.iso === 'br')!);
      setForm({
        name: '',
        loginId: '',
        password: '',
        confirmPassword: '',
        phoneDigits: '',
        acceptTerms: false
      });
    }
  }, [open]);

  const setField = <K extends keyof RegisterForm>(
    key: K,
    value: RegisterForm[K]
  ) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const validateForm = () => {
    const name = form.name.trim();
    const loginId = form.loginId.trim();

    // Regex: letras, números e espaço apenas entre palavras
    const nameRegex = /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/;

    if (name.length < 5)
      return 'Informe nome (mín. 5 caracteres).';

    if (name.length > 30)
      return 'Informe nome (máx. 30 caracteres).';

    if (!nameRegex.test(name))
      return 'Nome pode conter apenas letras, números e espaços (sem símbolos).';

    if (loginId.length < 7)
      return 'Informe um ID (mín. 7 caracteres).';

    if (loginId.length > 16)
      return 'Informe um ID (máx. 16 caracteres).';

    if (form.password.length < 6)
      return 'Senha muito curta (mín. 6 caracteres).';

    if (form.password !== form.confirmPassword)
      return 'As senhas não conferem.';

    if (!phoneValid)
      return 'Enter a valid phone number / Informe um celular válido.';

    if (!form.acceptTerms)
      return 'Você precisa aceitar os termos para continuar.';

    return null;
  };
  const requestCode = async () => {
    setError(null);

    const err = validateForm();
    if (err) {
      setError(err);
      return;
    }

    try {
      setLoading(true);

      const res = await registerRequest({
        Username: payload.loginId,
        Password: payload.password,
        Name: payload.name,
        Cellphone: payload.phoneE164,
        Code: null
      });

      if (res?.status === '1') {
        setStep('code');
        setOtp('');
        applyCooldown(res.retryAfterSeconds ?? 60);
        return;
      }

      if (res?.status === '3') {
        // ✅ SUCESSO: mostra dialog final (não fecha)
        setStep('success');
        return;
      }

      const msg = res?.message || 'Não foi possível enviar o código.';
      setError(msg);
      showError({ title: 'Falha ao enviar código', message: msg });
    } catch (e: any) {
      const msg =
        e?.Message ||
        e?.message ||
        e?.error ||
        'Não foi possível enviar o código. Tente novamente.';

      if (typeof e?.retryAfterSeconds === 'number') applyCooldown(e.retryAfterSeconds);

      setError(msg);
      showError({ title: 'Falha ao enviar código', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError(null);

    if (!otpValid) {
      setError('Digite o código completo (6 dígitos).');
      return;
    }

    try {
      setLoading(true);

      const resVerify = await registerRequest({
        Username: payload.loginId,
        Password: payload.password,
        Name: payload.name,
        Cellphone: payload.phoneE164,
        Code: otp
      });

      if (resVerify?.status === '3') {
        setStep('success');
        return;
      }

      if (resVerify?.status !== '2') {
        const msg = resVerify?.message || 'Código inválido. Tente novamente.';
        setError(msg);
        showError({ title: 'Falha na verificação', message: msg });
        return;
      }

      const resCreate = await registerRequest({
        Username: payload.loginId,
        Password: payload.password,
        Name: payload.name,
        Cellphone: payload.phoneE164,
        Code: null
      });

      if (resCreate?.status === '3') {
        setStep('success');
        return;
      }

      const msg = resCreate?.message || 'Não foi possível concluir o cadastro.';
      setError(msg);
      showError({ title: 'Falha ao concluir', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Falha na verificação. Tente novamente.';
      if (typeof e?.retryAfterSeconds === 'number') applyCooldown(e.retryAfterSeconds);
      setError(msg);
      showError({ title: 'Falha na verificação', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    await requestCode();
  };

  const closeAll = () => setOpenRegister(false);

  return (
    <>
    <Dialog open={open} onOpenChange={setOpenRegister}>
      <DialogContent
        onClose={closeAll}
        // ✅ no celular: full-screen (sem padding/margin de modal)
        className="
          relative  overflow-auto sm:overflow-hidden border border-white/10 bg-black/55 text-white backdrop-blur-xl
          w-screen h-[100dvh] rounded-none p-5
          sm:w-[min(560px,92vw)] sm:h-auto sm:rounded-[1.4rem] sm:p-6
        "
      >
        {/* Glow cinematic */}
        <div className="pointer-events-none absolute -inset-10 -z-10">
          <div className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00E1D7]/10 blur-3xl animate-[dlgCyan_3s_ease-in-out_infinite]" />
          <div className="absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FA0000]/8 blur-3xl animate-[dlgRed_4.2s_ease-in-out_infinite]" />
        </div>

        <DialogHeader>
          <div className="flex flex-row items-center gap-3 px-1 pt-3">
            <img
              src="/assets/icon.webp?v=20260926"
              className="h-11 w-11 drop-shadow-[0_0_35px_rgba(250,0,0,0.25)]"
              alt="Icon"
            />
            <div className="flex flex-col justify-center">
              <DialogTitle className="text-2xl font-extrabold tracking-tight">
                {step === 'form'
                  ? 'Criar Conta'
                  : step === 'code'
                    ? 'Verificar código'
                    : 'Tudo certo'}
              </DialogTitle>

              <DialogDescription className="text-white/65">
                {step === 'form'
                  ? 'Preencha os dados abaixo para criar sua conta no Cabal Liberty.'
                  : step === 'code'
                    ? `Enviamos um código para ${phoneE164}. Digite abaixo.`
                    : 'Cadastro recebido com sucesso.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* =================== CONTENT =================== */}
        {step === 'form' ? (
          <div className="grid gap-3">
            {/* Nome */}
            <div className="grid gap-1.5">
              <label className="text-xs font-bold text-white/90 tracking-wide">Primeiro Nome</label>
              <input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Primeiro nome"
                className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
              />
            </div>

            {/* ID */}
            <div className="grid gap-1.5">
              <label className="text-xs font-bold text-white/90 tracking-wide">ID</label>
              <input
                value={form.loginId}
                onChange={(e) => setField('loginId', e.target.value)}
                placeholder="Seu ID (login)"
                className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
              />
            </div>

            {/* Senha + Confirmar */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid gap-1.5">
                <label className="text-xs font-bold text-white/90 tracking-wide">Senha</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder="••••••••"
                  className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                />
              </div>

              <div className="grid gap-1.5">
                <label className="text-xs font-bold text-white/90 tracking-wide">Confirmar senha</label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setField('confirmPassword', e.target.value)}
                  placeholder="••••••••"
                  className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                />
              </div>
            </div>

            {/* Celular */}
            <div className="grid gap-1.5">
              <label className="text-xs font-bold text-white/90 tracking-wide">Phone / Celular</label>
              <div className="flex items-center gap-2">
                <CountrySelector
                  value={country}
                  onChange={(c) => { setCountry(c); setField('phoneDigits', ''); }}
                />

                <input
                  value={phoneMasked}
                  onChange={(e) => {
                    const d = onlyDigits(e.target.value).slice(0, country.maxDigits);
                    setField('phoneDigits', d);
                  }}
                  inputMode="tel"
                  placeholder={phonePlaceholder(country)}
                  className="h-12 w-full rounded-xl border border-white/20 bg-white/8 px-4 text-base font-semibold text-white outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-[#00E1D7]/70 focus:ring-2 focus:ring-[#00E1D7]/25 hover:border-white/30 transition"
                />
              </div>
              <div className="text-[11px] text-white/45">We'll send a verification code / Enviaremos um código.</div>
            </div>

            {/* Termos */}
            <label className="mt-1 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10">
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => setField('acceptTerms', e.target.checked)}
                className="
                  mt-1 h-4 w-4 shrink-0 rounded
                  border border-white/30 bg-white/5
                  transition
                  checked:bg-[#00E1D7] checked:border-[#00E1D7]
                  focus:outline-none
                "
              />
              <div className="leading-snug">
                <div className="text-sm font-bold text-white/85">Confirmo os termos</div>
                <div className="text-xs text-white/55">
                  Li e aceito os{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsInRegister(true); }}
                    className="text-[#00e1d7] hover:underline cursor-pointer font-semibold"
                  >
                    termos de uso
                  </button>
                  {' '}e política de privacidade.
                </div>
              </div>
            </label>
          </div>
        ) : step === 'code' ? (
          <div className="grid gap-4">
            <OTPInput value={otp} onChange={setOtp} disabled={loading} length={6} />

            <div className="flex items-center justify-between text-xs text-white/55">
              <button
                type="button"
                className="hover:text-white/80"
                onClick={() => setStep('form')}
                disabled={loading}
              >
                ← Voltar e editar
              </button>

              <button
                type="button"
                className={'hover:text-white/80 ' + (cooldown > 0 ? 'opacity-40 pointer-events-none' : '')}
                onClick={resend}
                disabled={loading || cooldown > 0}
              >
                {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
              </button>
            </div>
          </div>
        ) : (
          // ✅ SUCCESS SCREEN (status == 3)
          <div className="mt-2 grid gap-4">
            <div className="rounded-2xl border border-white/10 p-4">
              <div className="text-base font-extrabold text-white/90">
                Cadastro realizado com sucesso
              </div>
              <div className="mt-2 text-sm text-white/70 leading-relaxed">
                Dentro dos próximos dias você será notificado do lançamento de Cabal Liberty.
              </div>
            </div>

            {/* Botão central “estilo footer whatsapp” */}
            <a
              href={whatsappGroupUrl}
              target="_blank"
              rel="noreferrer"
              className="flex justify-center"
            >
              <Button
                className="
                text-white/90
                  relative overflow-hidden
                  w-full sm:w-[320px]
                  rounded-2xl px-5 py-4
                  font-extrabold
                  bg-white/10 hover:bg-white/15
                  border border-white/10
                  shadow-[0_0_40px_rgba(0,225,215,0.10)]
                "
              >
                <span className="inline-flex items-center gap-2">
                  {/* ícone whatsapp */}
                  <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" className="text-[#00E1D7]">
                    <path d="M19.11 17.19c-.26-.13-1.55-.76-1.79-.85-.24-.09-.41-.13-.59.13-.18.26-.68.85-.83 1.03-.15.18-.31.2-.57.07-.26-.13-1.1-.4-2.09-1.28-.77-.68-1.29-1.52-1.44-1.78-.15-.26-.02-.4.11-.53.11-.11.26-.31.39-.46.13-.15.18-.26.26-.44.09-.18.04-.33-.02-.46-.07-.13-.59-1.42-.81-1.94-.21-.51-.42-.44-.59-.44h-.5c-.18 0-.46.07-.7.33-.24.26-.92.9-.92 2.19 0 1.29.94 2.54 1.07 2.72.13.18 1.86 2.84 4.51 3.98.63.27 1.12.43 1.5.55.63.2 1.21.17 1.67.1.51-.08 1.55-.63 1.77-1.24.22-.61.22-1.13.15-1.24-.07-.11-.24-.18-.5-.31zM16 3C8.83 3 3 8.83 3 16c0 2.29.6 4.44 1.66 6.31L3 29l6.9-1.61A12.92 12.92 0 0 0 16 29c7.17 0 13-5.83 13-13S23.17 3 16 3zm0 23.5c-2.01 0-3.88-.59-5.46-1.6l-.39-.24-4.09.95.97-3.99-.25-.41A10.46 10.46 0 0 1 5.5 16C5.5 10.21 10.21 5.5 16 5.5S26.5 10.21 26.5 16 21.79 26.5 16 26.5z" />
                  </svg>
                  Entrar no grupo do WhatsApp
                </span>
              </Button>
            </a>

            <div className="flex justify-center">
              <button
                type="button"
                className="text-xs text-white/55 hover:text-white/80"
                onClick={closeAll}
              >
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* error */}
        {error && (
          <div className="mt-3 rounded-xl border border-[#FA0000]/30 bg-[#FA0000]/10 px-4 py-3 text-sm text-[#ffd0d0]">
            {error}
          </div>
        )}

        {/* =================== FOOTER ACTIONS =================== */}
        <DialogFooter className="mt-2">
          {step === 'success' ? (
            // no success, não mostra cancelar/confirmar do fluxo
            <div className="w-full" />
          ) : (
            <>
              <Button
                variant="ghost"
                className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10"
                onClick={closeAll}
                disabled={loading}
              >
                Cancelar
              </Button>

              {step === 'form' ? (
                <Button className="rounded-xl font-extrabold" onClick={requestCode} disabled={loading}>
                  {loading ? 'Enviando...' : 'Verificar celular'}
                </Button>
              ) : (
                <Button
                  className="rounded-xl font-extrabold"
                  onClick={verifyCode}
                  disabled={!otpValid || loading}
                >
                  {loading ? 'Validando...' : 'Confirmar cadastro'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>

        <style>{`
          @keyframes dlgCyan {
            0%,100% { opacity: .25; transform: translate(-50%,-50%) scale(1); }
            50% { opacity: .55; transform: translate(-50%,-50%) scale(1.05); }
          }
          @keyframes dlgRed {
            0%,100% { opacity: .18; transform: translate(-50%,-50%) scale(.98); }
            50% { opacity: .40; transform: translate(-50%,-50%) scale(1.06); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
    <TermsDialog open={showTermsInRegister} onClose={() => setShowTermsInRegister(false)} />
    </>
  );
}
